import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AgenteHorario,
  asignarCiclo,
  AsignacionCiclo,
  Ciclo,
  desasociarAsignacion,
  eliminarAsignacion,
  getAgentesHorarios,
  getAsignaciones,
  getCiclos,
} from '../services/horarios.service';
import { getErrorMessage } from '../utils/errorHandler';

const AsignacionTurnosPage: React.FC = () => {
  const [agentes, setAgentes] = useState<AgenteHorario[]>([]);
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [asignaciones, setAsignaciones] = useState<AsignacionCiclo[]>([]);
  const [selectedAgentes, setSelectedAgentes] = useState<string[]>([]);
  const [selectedCiclo, setSelectedCiclo] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [estadoFiltro, setEstadoFiltro] = useState<'TODAS' | 'ACTIVAS' | 'INACTIVAS'>('TODAS');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentesResp, ciclosResp, asignacionesResp] = await Promise.all([
        getAgentesHorarios(),
        getCiclos(),
        getAsignaciones(),
      ]);
      const agentesData = Array.isArray(agentesResp.data) ? agentesResp.data : [];
      const ciclosData = Array.isArray(ciclosResp.data) ? ciclosResp.data : [];
      setAgentes(agentesData);
      setCiclos(ciclosData);
      setAsignaciones(Array.isArray(asignacionesResp.data) ? asignacionesResp.data : []);
      setSelectedCiclo((prev) => (prev || (ciclosData[0]?._id ?? '')));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredAgentes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agentes;
    return agentes.filter((agente) => {
      const target = `${agente.nombre} ${agente.documento || ''} ${agente.idAgente || ''}`.toLowerCase();
      return target.includes(q);
    });
  }, [agentes, search]);

  const filteredAsignaciones = useMemo(() => {
    if (estadoFiltro === 'ACTIVAS') {
      return asignaciones.filter((item) => item.activo);
    }
    if (estadoFiltro === 'INACTIVAS') {
      return asignaciones.filter((item) => !item.activo);
    }
    return asignaciones;
  }, [asignaciones, estadoFiltro]);

  const toggleAgente = (id: string) => {
    setSelectedAgentes((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const onAsignar = async () => {
    if (!selectedCiclo) {
      setError('Seleccione un ciclo');
      return;
    }
    if (selectedAgentes.length === 0) {
      setError('Seleccione al menos un agente');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await asignarCiclo(selectedCiclo, selectedAgentes);
      setSelectedAgentes([]);
      setSuccess('Ciclo asignado correctamente');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onDesasociar = async (id: string) => {
    setProcessingId(id);
    setError('');
    setSuccess('');
    try {
      await desasociarAsignacion(id);
      setSuccess('Relacion desasociada correctamente');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProcessingId(null);
    }
  };

  const onEliminar = async (id: string) => {
    if (!window.confirm('Eliminar la relacion de asignacion?')) return;
    setProcessingId(id);
    setError('');
    setSuccess('');
    try {
      await eliminarAsignacion(id);
      setSuccess('Relacion eliminada correctamente');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProcessingId(null);
    }
  };

  const getAgenteNombre = (agente: AsignacionCiclo['agenteId']): string => {
    if (typeof agente === 'string') return agente;
    return agente.nombre;
  };

  const getAgenteDni = (agente: AsignacionCiclo['agenteId']): string => {
    if (typeof agente === 'string') return '-';
    return agente.documento || '-';
  };

  const getCicloNombre = (ciclo: AsignacionCiclo['cicloId']): string => {
    if (typeof ciclo === 'string') return ciclo;
    return ciclo.nombre;
  };

  return (
    <>
      <h1 className="page-title">Turnos - Asignacion</h1>

      <div className="card">
        <h2 className="card-title">Asignar ciclo a agentes</h2>
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}

        <div className="form-grid">
          <div className="form-group">
            <label>Ciclo</label>
            <select value={selectedCiclo} onChange={(e) => setSelectedCiclo(e.target.value)}>
              <option value="">Seleccionar...</option>
              {ciclos.map((ciclo) => (
                <option key={ciclo._id} value={ciclo._id}>
                  {ciclo.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Buscar agente</label>
            <input
              placeholder="Nombre, DNI o ID agente"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper" style={{ marginBottom: '12px' }}>
          <table>
            <thead>
              <tr>
                <th>Sel</th>
                <th>Nombre</th>
                <th>DNI</th>
                <th>ID_Agente</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgentes.map((agente) => (
                <tr key={agente._id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedAgentes.includes(agente._id)}
                      onChange={() => toggleAgente(agente._id)}
                    />
                  </td>
                  <td>{agente.nombre}</td>
                  <td>{agente.documento || '-'}</td>
                  <td>{agente.idAgente || '-'}</td>
                </tr>
              ))}
              {filteredAgentes.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center' }}>No hay colaboradores para mostrar</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <button className="btn btn-primary" type="button" disabled={saving || loading} onClick={onAsignar}>
          {saving ? 'Asignando...' : 'Asignar ciclo'}
        </button>
      </div>

      <div className="card">
        <h2 className="card-title">Relaciones ciclo-agente</h2>
        <div className="form-grid" style={{ marginBottom: '10px' }}>
          <div className="form-group" style={{ maxWidth: '260px' }}>
            <label>Filtrar por estado</label>
            <select value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value as 'TODAS' | 'ACTIVAS' | 'INACTIVAS')}>
              <option value="TODAS">Todas</option>
              <option value="ACTIVAS">Activas</option>
              <option value="INACTIVAS">Inactivas</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : filteredAsignaciones.length === 0 ? (
          <div className="empty-state">No hay asignaciones registradas.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Agente</th>
                  <th>DNI</th>
                  <th>Ciclo</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredAsignaciones.map((asignacion) => (
                  <tr key={asignacion._id}>
                    <td>{getAgenteNombre(asignacion.agenteId)}</td>
                    <td>{getAgenteDni(asignacion.agenteId)}</td>
                    <td>{getCicloNombre(asignacion.cicloId)}</td>
                    <td>
                      <span className={`badge ${asignacion.activo ? 'badge-success' : 'badge-secondary'}`}>
                        {asignacion.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        {asignacion.activo && (
                          <button
                            className="btn btn-outline btn-sm"
                            type="button"
                            disabled={processingId === asignacion._id}
                            onClick={() => onDesasociar(asignacion._id)}
                          >
                            {processingId === asignacion._id ? 'Procesando...' : 'Desasociar'}
                          </button>
                        )}
                        <button
                          className="btn btn-danger btn-sm"
                          type="button"
                          disabled={processingId === asignacion._id}
                          onClick={() => onEliminar(asignacion._id)}
                        >
                          {processingId === asignacion._id ? 'Procesando...' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AsignacionTurnosPage;
